import { FC, useCallback, useEffect, useState } from 'react';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, message, Modal } from 'antd';
import type { FormTemplate } from '../../models/TaskForm/TaskForm';
import {
  createFormTemplate,
  deleteFormTemplate,
  getTaskFormManagementTemplates,
  getUsersByIds,
  selectTaskFormManagementTemplates,
  selectTaskFormManagementTemplatesStatus,
  selectTaskFormManagementTemplatesTotalCount,
  selectTaskFormUserNicknamesById,
  TaskFormTemplatesRequest,
} from '../../store/reducers/TaskFormSlice';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { usePermissions } from '../../hooks/shared/usePermissions';
import { useDebounce } from '../../hooks/shared/useDebounce';
import Blocks from '../Blocks/Blocks';
import blockStyles from '../Blocks/style.module.css';
import SiteMenu from '../SiteMenu/SiteMenu';
import {
  DEFAULT_TABLE_REQUEST,
  QuestionnaireTemplatesTable,
} from './QuestionnaireTemplatesTable';
import { TemplateEditorModal } from './TemplateEditorModal';
import {
  buildCopiedTemplateRequest,
  getCopiedTemplateQuestionDrafts,
} from './templateCopy';
export {
  buildCopiedTemplateRequest,
  getCopiedTemplateName,
  getCopiedTemplateQuestionDrafts,
} from './templateCopy';
import styles from './questionnaireTemplatesList.module.css';

const getActionErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const errorMessage = (error as { message?: unknown }).message;
    if (typeof errorMessage === 'string') return errorMessage;
  }

  return fallback;
};

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'view'; template: FormTemplate }
  | { type: 'delete'; templateId: string; templateName: string };

export const QuestionnaireTemplatesList: FC = () => {
  const dispatch = useAppDispatch();
  const permissions = usePermissions();
  const templates = useAppSelector(selectTaskFormManagementTemplates);
  const templatesTotalCount = useAppSelector(
    selectTaskFormManagementTemplatesTotalCount,
  );
  const templatesStatus = useAppSelector(
    selectTaskFormManagementTemplatesStatus,
  );
  const userNicknamesById = useAppSelector(selectTaskFormUserNicknamesById);

  const [tableRequest, setTableRequest] = useState<TaskFormTemplatesRequest>(
    DEFAULT_TABLE_REQUEST,
  );
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 500);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [deleting, setDeleting] = useState(false);
  const [copyingTemplateIds, setCopyingTemplateIds] = useState<Set<string>>(
    () => new Set(),
  );
  const canManageTemplates = permissions.canManageSurveyTemplates;

  useEffect(() => {
    setTableRequest((current) =>
      current['Filter.TextSearch'] === debouncedSearchText
        ? current
        : {
            ...current,
            PageIndex: 0,
            'Filter.TextSearch': debouncedSearchText,
          },
    );
  }, [debouncedSearchText]);

  useEffect(() => {
    dispatch(getTaskFormManagementTemplates(tableRequest));
  }, [dispatch, tableRequest]);

  useEffect(() => {
    const missingUserIds = Array.from(
      new Set(
        templates
          .map((template) => template.createdUserId)
          .filter(
            (userId): userId is string =>
              Boolean(userId) && !userNicknamesById[userId as string],
          ),
      ),
    );

    if (missingUserIds.length > 0) {
      dispatch(getUsersByIds(missingUserIds));
    }
  }, [dispatch, templates, userNicknamesById]);

  const refreshTemplates = useCallback(() => {
    dispatch(getTaskFormManagementTemplates(tableRequest));
  }, [dispatch, tableRequest]);

  const handleCopy = useCallback(
    async (template: FormTemplate) => {
      if (!canManageTemplates || copyingTemplateIds.has(template.id)) return;

      const copiedQuestions = getCopiedTemplateQuestionDrafts(template);

      if (copiedQuestions.length === 0) {
        message.error('Невозможно создать копию шаблона без вопросов');
        return;
      }

      const payload = buildCopiedTemplateRequest(template, templates);

      setCopyingTemplateIds((current) => new Set(current).add(template.id));

      try {
        await dispatch(createFormTemplate(payload)).unwrap();
        message.success('Копия шаблона анкеты создана');
        refreshTemplates();
      } catch (error) {
        message.error(
          getActionErrorMessage(
            error,
            'Не удалось создать копию шаблона анкеты',
          ),
        );
      } finally {
        setCopyingTemplateIds((current) => {
          const next = new Set(current);
          next.delete(template.id);
          return next;
        });
      }
    },
    [
      canManageTemplates,
      copyingTemplateIds,
      dispatch,
      refreshTemplates,
      templates,
    ],
  );

  const handleDelete = useCallback(async () => {
    if (modal.type !== 'delete' || deleting || !canManageTemplates) return;

    const currentPageIndex = tableRequest.PageIndex ?? 0;
    const currentPageSize =
      tableRequest.PageSize ?? DEFAULT_TABLE_REQUEST.PageSize;
    const shouldMoveToPreviousPage =
      templates.length === 1 && currentPageIndex > 0;

    setDeleting(true);
    try {
      await dispatch(deleteFormTemplate(modal.templateId)).unwrap();
      message.success('Шаблон анкеты удалён');
      setModal({ type: 'none' });

      if (shouldMoveToPreviousPage) {
        setTableRequest((current) => ({
          ...current,
          PageIndex: Math.max(0, currentPageIndex - 1),
          PageSize: currentPageSize,
        }));
      } else {
        refreshTemplates();
      }
    } catch (error) {
      message.error(
        getActionErrorMessage(error, 'Не удалось удалить шаблон анкеты'),
      );
    } finally {
      setDeleting(false);
    }
  }, [
    canManageTemplates,
    deleting,
    dispatch,
    modal,
    refreshTemplates,
    templates.length,
    tableRequest.PageIndex,
    tableRequest.PageSize,
  ]);

  const closeModal = () => setModal({ type: 'none' });

  return (
    <>
      <Blocks
        topBlock={
          <div className={blockStyles.topBlockGridCompact}>
            <SiteMenu
              title="Шаблоны анкет"
              subtitle="Управление шаблонами анкет для обзвона пациентов"
              svg
              svgType="questionnaireTemplates"
            />
            <div className={styles.topControls}>
              <Input
                allowClear
                className={styles.search}
                placeholder="Поиск по названию"
                value={searchText}
                data-testid="questionnaire-template-search"
                onChange={(event) => setSearchText(event.target.value)}
                prefix={
                  <SearchOutlined
                    style={{ color: '#737373BF', fontSize: 16 }}
                  />
                }
              />
              {canManageTemplates && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={templatesStatus.responseLoading}
                  onClick={() => setModal({ type: 'create' })}
                  data-testid="create-template-button"
                >
                  Создать анкету
                </Button>
              )}
            </div>
          </div>
        }
        mainBlock={
          <QuestionnaireTemplatesTable
            templates={templates}
            totalCount={templatesTotalCount}
            status={templatesStatus}
            userNicknamesById={userNicknamesById}
            request={tableRequest}
            onRequestChange={setTableRequest}
            actions={{
              canManage: canManageTemplates,
              deleting,
              copyingIds: copyingTemplateIds,
              onCopy: handleCopy,
              onDelete: (templateId, templateName) =>
                setModal({ type: 'delete', templateId, templateName }),
              onOpen: (template) => setModal({ type: 'view', template }),
            }}
          />
        }
      />

      <TemplateEditorModal
        open={modal.type === 'create' || modal.type === 'view'}
        template={modal.type === 'view' ? modal.template : null}
        onClose={closeModal}
        onSaved={refreshTemplates}
      />

      <Modal
        title="Подтверждение удаления"
        open={modal.type === 'delete'}
        onCancel={() => {
          if (!deleting) closeModal();
        }}
        onOk={handleDelete}
        confirmLoading={deleting}
        okText="Удалить"
        cancelText="Отмена"
        okButtonProps={{ danger: true, disabled: deleting }}
        cancelButtonProps={{ disabled: deleting }}
      >
        <p>
          Вы действительно хотите удалить анкету{' '}
          <strong>{modal.type === 'delete' ? modal.templateName : ''}</strong>?
        </p>
      </Modal>
    </>
  );
};

QuestionnaireTemplatesList.displayName = 'QuestionnaireTemplatesList';
